import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api';
import { useToast } from '../../toast';
import { useSession } from '../../session';
import { useWorkspace, type Role } from '../Workspace';

interface Member {
  userId: string;
  email: string;
  displayName: string;
  role: Role;
  status: 'active' | 'deactivated';
  joinedAt: string;
  factors: { totp: boolean; passkeys: number };
}

interface Invitation {
  id: string;
  email: string;
  role: Role;
  expiresAt: string;
}

/**
 * The people in a workspace. Inviting comes first for an admin, since it is the thing most visits here
 * are for; the list of who is already in follows, then anyone invited who has not yet accepted.
 */
export function WorkspaceMembers() {
  const { tenantId, role, canManage } = useWorkspace();
  const { me } = useSession();
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('member');
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const m = await api<{ members: Member[] }>(`/api/tenants/${tenantId}/members`);
      setMembers(m.members);
      if (role !== 'member') {
        const i = await api<{ invitations: Invitation[] }>(`/api/tenants/${tenantId}/invitations`);
        setInvitations(i.invitations);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the people here');
    }
  }, [tenantId, role]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>, done?: string) {
    setError(null);
    try {
      await fn();
      if (done) toast.ok(done);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That did not work');
    }
  }

  function invite(e: FormEvent) {
    e.preventDefault();
    const address = inviteEmail;
    setInviteLink(null);
    void act(async () => {
      const r = await api<{ emailSent: boolean; link?: string }>(`/api/tenants/${tenantId}/invitations`, {
        method: 'POST',
        body: { email: address, role: inviteRole },
      });
      // The invitation is valid either way; when email is not configured or the provider refuses it,
      // the server hands back the link so it can be passed on by hand.
      if (r.emailSent) toast.ok(`Invitation sent to ${address}`);
      else setInviteLink(r.link ?? null);
    }).then(() => setInviteEmail(''));
  }

  return (
    <>
      {error && <div className="alert">{error}</div>}

      {canManage && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3>Invite someone</h3>
          <form onSubmit={invite}>
            <div className="row">
              <input
                id="invite-email"
                type="email"
                required
                aria-label="Email address"
                placeholder="name@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <select
                id="invite-role"
                aria-label="Role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as Role)}
              >
                <option value="member">member</option>
                <option value="admin">admin</option>
                {role === 'owner' && <option value="owner">owner</option>}
              </select>
              <button type="submit">Send invitation</button>
            </div>
          </form>
          {inviteLink && (
            <div className="warn" style={{ margin: '16px 0 0' }}>
              <p className="small" style={{ marginTop: 0 }}>
                The invitation was created, but the email could not be sent. Send this link to them yourself -
                it works once, and the expiry date is shown under pending invitations.
              </p>
              <input readOnly value={inviteLink} onFocus={(e) => e.currentTarget.select()} style={{ width: '100%' }} />
            </div>
          )}
        </div>
      )}

      <div className="card flush">
        <table>
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              <th>Sign-in</th>
              <th>Status</th>
              {canManage && <th className="actions-cell"></th>}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const isMe = m.userId === me?.user.id;
              const mayTouch = canManage && !isMe && (m.role !== 'owner' || role === 'owner');
              return (
                <tr key={m.userId}>
                  <td>
                    {m.displayName || m.email}
                    <br />
                    <span className="small muted">{m.email}</span>
                  </td>
                  <td>
                    {role === 'owner' && !isMe && m.status === 'active' ? (
                      <select
                        aria-label={`Role for ${m.displayName || m.email}`}
                        value={m.role}
                        onChange={(e) =>
                          act(() =>
                            api(`/api/tenants/${tenantId}/members/${m.userId}`, {
                              method: 'PATCH',
                              body: { role: e.target.value },
                            }),
                          )
                        }
                      >
                        <option value="owner">owner</option>
                        <option value="admin">admin</option>
                        <option value="member">member</option>
                      </select>
                    ) : (
                      m.role
                    )}
                  </td>
                  <td>
                    <span className={`pill ${m.factors.totp ? 'on' : 'off'}`}>authenticator</span>{' '}
                    <span className={`pill ${m.factors.passkeys ? 'on' : 'off'}`}>
                      {m.factors.passkeys} passkey{m.factors.passkeys === 1 ? '' : 's'}
                    </span>
                  </td>
                  <td>
                    <span className={`pill ${m.status === 'active' ? 'on' : 'off'}`}>{m.status}</span>
                  </td>
                  {canManage && (
                    <td className="actions-cell">
                      {mayTouch && m.status === 'active' && (
                        <button
                          type="button"
                          className="danger"
                          onClick={() =>
                            act(
                              () => api(`/api/tenants/${tenantId}/members/${m.userId}/deactivate`, { method: 'POST' }),
                              `${m.displayName || m.email} deactivated. Their org access ends within a minute.`,
                            )
                          }
                        >
                          Deactivate
                        </button>
                      )}
                      {mayTouch && m.status === 'deactivated' && (
                        <button
                          type="button"
                          className="secondary"
                          onClick={() =>
                            act(() => api(`/api/tenants/${tenantId}/members/${m.userId}/reactivate`, { method: 'POST' }))
                          }
                        >
                          Reactivate
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {members.length === 0 && !error && (
              <tr>
                <td colSpan={canManage ? 5 : 4} className="empty">Loading…</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canManage && invitations.length > 0 && (
        <>
          <h2>Pending invitations</h2>
          <div className="card flush">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Expires</th>
                  <th className="actions-cell"></th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((i) => (
                  <tr key={i.id}>
                    <td>{i.email}</td>
                    <td>{i.role}</td>
                    <td>{new Date(i.expiresAt).toLocaleDateString()}</td>
                    <td className="actions-cell">
                      <button
                        type="button"
                        className="secondary"
                        onClick={() =>
                          act(() => api(`/api/tenants/${tenantId}/invitations/${i.id}`, { method: 'DELETE' }))
                        }
                      >
                        Cancel invitation
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
